import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import FileDropZone from '../../../UserProfile/Documents/components/FileDropZone';
import ImageLightbox from '../../../../components/ui/ImageLightbox';

export interface MatchAttachmentFieldState {
  file: File | null;
  remove: boolean;
  dirty: boolean;
}

interface MatchAttachmentFieldProps {
  existingUrl?: string | null;
  onChange: (state: MatchAttachmentFieldState) => void;
  disabled?: boolean;
  className?: string;
}

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

const MatchAttachmentField: React.FC<MatchAttachmentFieldProps> = ({
  existingUrl,
  onChange,
  disabled,
  className = '',
}) => {
  const { t } = useTranslation('tournament');
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [markedForRemoval, setMarkedForRemoval] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Track object URL lifecycle
  useEffect(() => {
    if (stagedFile) {
      const url = URL.createObjectURL(stagedFile);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setObjectUrl(null);
    return undefined;
  }, [stagedFile]);

  // Emit state upward
  useEffect(() => {
    const dirty = stagedFile != null || markedForRemoval;
    onChange({ file: stagedFile, remove: markedForRemoval && !stagedFile, dirty });
  }, [stagedFile, markedForRemoval, onChange]);

  const handleFilesSelected = (files: File[]) => {
    if (files.length > 0) {
      setStagedFile(files[0]);
      setMarkedForRemoval(false);
    }
  };

  const handleCameraInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && ALLOWED_MIME.includes(f.type)) {
      setStagedFile(f);
      setMarkedForRemoval(false);
    }
    e.target.value = '';
  };

  const handleRemove = () => {
    setStagedFile(null);
    if (existingUrl) {
      setMarkedForRemoval(true);
    } else {
      setMarkedForRemoval(false);
    }
  };

  const previewUrl = objectUrl ?? (!markedForRemoval ? existingUrl ?? null : null);
  const hasSomething = !!previewUrl;

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('Match Attachment')}
      </label>

      {hasSomething ? (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="group relative shrink-0 overflow-hidden rounded-md border border-border focus-visible:ring-2 focus-visible:ring-primary outline-none"
            aria-label={t('Enlarge') as string}
            disabled={disabled}
          >
            <img
              src={previewUrl!}
              alt={t('Match Attachment') as string}
              className="h-24 w-24 object-cover transition-transform group-hover:scale-105"
              loading="lazy"
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = ''; }}
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
              <Icon name="ZoomIn" size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-2">
              {stagedFile
                ? (stagedFile.name + ' • ' + (stagedFile.size / (1024 * 1024)).toFixed(2) + ' MB')
                : t('Current image')}
            </p>
            <div className="flex flex-wrap gap-2">
              <FileDropZone
                compact
                accept={ALLOWED_MIME.join(',') + ',.jpg,.jpeg,.png,.webp'}
                allowedMimeTypes={ALLOWED_MIME}
                sizeFeatureKey="matchAttachmentMaxSizeMB"
                typeErrorLabel={t('Only JPG, PNG, and WebP images are allowed') as string}
                onFilesSelected={handleFilesSelected}
                inputTestId="match-attachment-file-input"
              />
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              >
                <Icon name="Camera" size={14} />
                {t('Take Photo')}
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-destructive/40 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
              >
                <Icon name="Trash2" size={14} />
                {t('Remove Image')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <FileDropZone
            accept={ALLOWED_MIME.join(',') + ',.jpg,.jpeg,.png,.webp'}
            allowedMimeTypes={ALLOWED_MIME}
            sizeFeatureKey="matchAttachmentMaxSizeMB"
            typeErrorLabel={t('Only JPG, PNG, and WebP images are allowed') as string}
            placeholderHint={t('JPG, PNG, WebP') as string}
            onFilesSelected={handleFilesSelected}
            inputTestId="match-attachment-file-input"
          />
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Icon name="Camera" size={14} />
              {t('Take Photo')}
            </button>
          </div>
        </div>
      )}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={handleCameraInput}
      />

      <ImageLightbox
        src={previewUrl}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        alt={t('Match Attachment') as string}
      />
    </div>
  );
};

export default MatchAttachmentField;
